import React, { useState, useEffect } from 'react';
import { Alert, AlertIcon, AlertDescription, Box, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import constants from 'providers/Constants';

const SandboxAlert = () => {
  const { t } = useTranslation();
  const [currentEnv, setCurrentEnv] = useState(constants.getCurrentEnvironment());
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check environment on component mount and update visibility
    const environment = constants.getCurrentEnvironment();
    setCurrentEnv(environment);
    setIsVisible(environment === 'SANDBOX');

    // Update CSS custom property for alert height based on viewport
    if (environment === 'SANDBOX') {
      const isMobile = window.innerWidth <= 768;
      document.documentElement.style.setProperty('--sandbox-alert-height', isMobile ? '100px' : '60px');
    } else {
      document.documentElement.style.setProperty('--sandbox-alert-height', '0px');
    }
  }, []);

  useEffect(() => {
    // Listen for environment changes (in case user switches without reload)
    const checkEnvironment = () => {
      const environment = constants.getCurrentEnvironment();
      setCurrentEnv(environment);
      const shouldShow = environment === 'SANDBOX';
      setIsVisible(shouldShow);

      // Update CSS custom property for alert height based on viewport
      if (shouldShow) {
        const isMobile = window.innerWidth <= 768;
        document.documentElement.style.setProperty('--sandbox-alert-height', isMobile ? '100px' : '60px');
      } else {
        document.documentElement.style.setProperty('--sandbox-alert-height', '0px');
      }
    };

    // Check immediately
    checkEnvironment();

    // Set up interval to periodically check for environment changes
    const interval = setInterval(checkEnvironment, 1000);
    
    // Listen for window resize to update alert height on viewport change
    window.addEventListener('resize', checkEnvironment);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', checkEnvironment);
    };
  }, []);

  if (!isVisible || currentEnv !== 'SANDBOX') {
    return null;
  }

  return (
    <Box 
      position="fixed" 
      top="0" 
      left="0" 
      right="0" 
      zIndex="1001"
      w="100%"
      height={{ base: "auto", md: "60px" }}
      py={{ base: 0, md: 0 }}
      px={{ base: 0, md: 0 }}
    >
      <Alert 
        status="warning" 
        bg="orange.400" 
        color="white" 
        py={{ base: 2, md: 3 }} 
        px={{ base: 2, md: 4 }}
        height="100%"
        flexDirection={{ base: 'column', md: 'row' }}
        alignItems={{ base: 'flex-start', md: 'center' }}
        gap={{ base: 2, md: 0 }}
      >
        <AlertIcon color="white" flexShrink={0} mt={{ base: 0.5, md: 0 }} />
        <AlertDescription>
          <Text 
            fontWeight="bold" 
            fontSize={{ base: "xs", md: "sm" }}
            wordBreak="break-word"
          >
            ⚠️ {t('SANDBOX MODE')} - {t('You are currently in the testing environment. All transactions and operations are simulated and not real.')}
          </Text>
        </AlertDescription>
      </Alert>
    </Box>
  );
};

export default SandboxAlert;
